import {IThronVideoConfig} from './IThronVideo';

import {getAppOrigin} from 'coreUtils/url';
import {fetch, getFrontEndConfig} from 'coreUtils/utils';

import AbstractVideo from '../video/AbstractVideo';
import {TAspectRatioResponse} from '../video/IAbstractVideo';

/**
 * Represents ThronVideo
 */
class ThronVideo extends AbstractVideo{
	private readonly pkeyId: string;
	private readonly clientId: string;
	private readonly channel: string;
	private readonly subPath: string;
	private readonly thronClientId: string;
	private readonly thronSubPath: string;

	/**
	 * Constructor
	 * @constructor
	 * @param {IThronVideoConfig} config - config
	 */
	constructor(config: IThronVideoConfig) {
		const [videoId, pkey, channel] = config.videoId.split('/');

		super({
			videoId: videoId,
			aspectRatio: config.aspectRatio,
			thumbnail: config.thumbnail,
		});
		this.thronClientId = getFrontEndConfig()?.app?.thron?.editorial?.clientId;
		this.thronSubPath = getFrontEndConfig()?.app?.thron?.products?.subPath;
		this.pkeyId = pkey;
		this.clientId = config.clientId || this.thronClientId;
		this.subPath = config.subPath || this.thronSubPath || this.clientId;
		this.channel = config.channel || channel || 'WEBFULLHD';
	}

	/**
	 * Returns the video url
	 * @returns {string}
	*/
	getVideoUrl(): string {
		return `https://${this.clientId}-cdn.thron.com/delivery/public/video/${this.subPath}/${this.videoId}/${this.pkeyId}/${this.channel}/video`;
	}

	/**
		 * Returns the thumbnail url
		 * @param {object} dims - the dimensions to pass to Thron API
		 * @TODO: CHECK -> Thron Api seems to return the image not respecting the dimensions given:
		 *     - the image respects the size with the lowest vaule
		 *     - the image can't be cut passing both height and with (it mantain the same aspect ratio)
		 * @returns {string}
	*/
	getThumbnailUrl(dims: Record<string, number> = {height: 0, width: 0}): string {
		if (this.thumbnail) {
			return this.thumbnail;
		}

		const {height = 0, width = 0} = dims;
		let url = `${getAppOrigin()}/mobify/thron-video/thumbnail?videoId=${this.videoId}&pkey=${this.pkeyId}`;

		if (this.clientId !== this.thronClientId) {
			url += `&clientId=${this.clientId}`;
		}

		if (height && width) {
			url += `&height=${height}&width=${width}`;
		}

		return url;
	}

	/**
	 * Returns the original aspect ratio of the video selected by id
	 * @param {string} videoId - id of the video
	 * @returns {object} - response
	 */
	static async staticGetOriginalAspectRatio(videoId: string, pkeyId: string): Promise<TAspectRatioResponse> {
		return await fetch(`${getAppOrigin()}/mobify/thron-video/aspect-ratio?videoId=${videoId}&pkey=${pkeyId}`)
			.then((res) => (res as Response).json());
	}
}

export default ThronVideo;
